import { useSyncExternalStore } from 'react';
import { DEFAULT_DEPTH, ENGINE_HASH_MB } from './config';

/**
 * Per-viewer tuning for the browser engine: how many lines, how deep, how much memory.
 *
 * Like the on/off switch in `preference.ts`, this is a browser preference, not
 * account state — the right values depend on the device's CPU and RAM — so it lives
 * in `localStorage`, never reaches the API, and falls back to defaults when storage
 * is unavailable. Changes are broadcast to every subscriber in the tab (and to other
 * tabs through the `storage` event), so the engine and every panel follow at once.
 */

export interface EngineSettings {
  /** MultiPV: candidate lines the engine keeps, 1–5. */
  lines: number;
  /** Search depth in plies. */
  depth: number;
  /** Transposition table size in MiB. */
  hashMb: number;
}

export const ENGINE_SETTINGS_KEY = 'chessreps.engineSettings';

export const MIN_LINES = 1;
export const MAX_LINES = 5;
export const MIN_DEPTH = 10;
export const MAX_DEPTH = 30;
/** Hash sizes offered; the single-threaded WASM heap stays well inside 32-bit limits. */
export const HASH_OPTIONS_MB = [16, 32, 64, 128, 256] as const;

export const DEFAULT_ENGINE_SETTINGS: EngineSettings = {
  lines: 1,
  depth: DEFAULT_DEPTH,
  hashMb: ENGINE_HASH_MB,
};

function clampInt(value: unknown, min: number, max: number, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(max, Math.max(min, Math.round(value)));
}

/** Coerce anything (old versions, hand-edited storage) into valid settings. */
export function normalizeEngineSettings(raw: unknown): EngineSettings {
  const input = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const hash = input.hashMb;
  return {
    lines: clampInt(input.lines, MIN_LINES, MAX_LINES, DEFAULT_ENGINE_SETTINGS.lines),
    depth: clampInt(input.depth, MIN_DEPTH, MAX_DEPTH, DEFAULT_ENGINE_SETTINGS.depth),
    hashMb: (HASH_OPTIONS_MB as readonly unknown[]).includes(hash)
      ? (hash as number)
      : DEFAULT_ENGINE_SETTINGS.hashMb,
  };
}

/**
 * Hash sizes sensible for this device. `navigator.deviceMemory` (GiB, Chromium
 * only, capped at 8) keeps a low-memory phone from being offered 256 MiB; browsers
 * that do not report it get every option.
 */
export function hashOptionsForDevice(deviceMemoryGb?: number): number[] {
  const reported = deviceMemoryGb ?? readDeviceMemory();
  if (reported === undefined) return [...HASH_OPTIONS_MB];
  const cap = Math.max(HASH_OPTIONS_MB[0], reported * 32);
  return HASH_OPTIONS_MB.filter((size) => size <= cap);
}

function readDeviceMemory(): number | undefined {
  if (typeof navigator === 'undefined') return undefined;
  const value = (navigator as Navigator & { deviceMemory?: unknown }).deviceMemory;
  return typeof value === 'number' && value > 0 ? value : undefined;
}

function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function load(storage: Pick<Storage, 'getItem'> | null): EngineSettings {
  try {
    const raw = storage?.getItem(ENGINE_SETTINGS_KEY);
    return raw ? normalizeEngineSettings(JSON.parse(raw)) : DEFAULT_ENGINE_SETTINGS;
  } catch {
    return DEFAULT_ENGINE_SETTINGS;
  }
}

let current: EngineSettings | null = null;
const listeners = new Set<() => void>();

/** The current settings; stable identity until they change. */
export function readEngineSettings(): EngineSettings {
  if (current === null) current = load(safeStorage());
  return current;
}

/** Merge, validate, persist (best effort) and broadcast a change. */
export function writeEngineSettings(
  patch: Partial<EngineSettings>,
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): EngineSettings {
  const next = normalizeEngineSettings({ ...readEngineSettings(), ...patch });
  current = next;
  try {
    storage?.setItem(ENGINE_SETTINGS_KEY, JSON.stringify(next));
  } catch {
    // Unpersistable storage still keeps the change for this session.
  }
  for (const listener of [...listeners]) listener();
  return next;
}

export function resetEngineSettings(): EngineSettings {
  return writeEngineSettings(DEFAULT_ENGINE_SETTINGS);
}

export function subscribeEngineSettings(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

// Another tab changed the settings: pick them up here too.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key !== ENGINE_SETTINGS_KEY) return;
    current = null;
    for (const listener of [...listeners]) listener();
  });
}

export function useEngineSettings(): EngineSettings {
  return useSyncExternalStore(subscribeEngineSettings, readEngineSettings, readEngineSettings);
}

/** Forget the in-memory copy (tests). */
export function resetEngineSettingsCache(): void {
  current = null;
}
